/* eslint-disable ember/no-computed-properties-in-native-classes */
import Controller from '@ember/controller';
import { action, computed, set, setProperties } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Reportconfig, Reportgroup, User } from 'frontend-utils/models';
import {
  REPORT_TYPE_ALL,
  REPORT_TYPE_MARKETPARITY,
  REPORT_TYPE_MARKETRATES,
  REPORT_TYPE_PARITY,
  REPORT_TYPE_PARITY_INSIGHT,
  REPORT_TYPE_RATES,
  REPORT_TYPE_REVENUE_INSIGHT,
  TYPE_MARKET_INSIGHT,
} from 'frontend-utils/utils/constants/constant-values';
import type HotelSettingsReportsRoute from 'lumen/routes/hotel/settings/reports';
import type { ModelFrom } from 'lumen/utils/routes';
import { allSettled } from 'rsvp';

export default class HotelSettingsReportsController extends Controller {
  declare model: ModelFrom<HotelSettingsReportsRoute>;

  queryParams = [
    'ownReports',
    'type',
    'recipient',
    'hotelId',
    'marketId',
    'page',
    'sortBy',
    'sortDirection',
  ];

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];

  @tracked page = 1;
  @tracked rowSelection = [];

  arrReportsSelectedForBulkDeletion: string[] = [];
  hotelId = null;
  impersonateId = null;
  marketId = null;
  ownReports = true;
  recipient = null;
  selectedHotelOrMarket = null;
  selectedImpersonator = null;
  selectedRecipient = null;
  sortBy = 'name';
  sortDirection = 'asc';
  type = REPORT_TYPE_ALL;

  // eslint-disable-next-line ember/use-brace-expansion
  @computed('model.isLoading', 'model.reportgroups.@each.isDeleted')
  get convertedModel(): Reportgroup[] {
    // model contains an array of reportgroups:
    // reports in same reportgroup have some hotel, frequency,... Just other recipients
    // for now we'll pass the whole reportgroup to each table row
    if (!this.model?.isLoading && this.model?.reportgroups) {
      return this.model.reportgroups.filter(
        (reportGroup) => !reportGroup.isDeleted,
      );
    }

    // eslint-disable-next-line ember/no-side-effects
    set(this, 'arrReportsSelectedForBulkDeletion', []);

    return [];
  }

  @computed('model.isLoading', 'convertedModel.length')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get deletableReports() {
    const arrReportGroup: string[] = [];

    this.convertedModel.forEach((reportGroup) => {
      // Note: we need to check here that the user has edit rights to delete these reports
      // For individual selection we can do these checks in the cell, but for bulk deletion we do this here.
      if (this.access.can('report.delete', reportGroup)) {
        arrReportGroup.push(reportGroup.id!);
      }
    });

    return arrReportGroup;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get reportCategories() {
    return [
      {
        label: this.intl.t('reports.my_reports'),
        ownReports: true,
      },
      {
        label: this.intl.t('reports.all_reports'),
        ownReports: false,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get reportTypes() {
    return [
      {
        type: REPORT_TYPE_ALL,
        label: this.intl.t('reports.type.all'),
      },
      {
        type: TYPE_MARKET_INSIGHT,
        label: this.intl.t('reports.type.market_insight'),
      },
      {
        type: REPORT_TYPE_RATES,
        label: this.intl.t('reports.type.hotel_rates'),
      },
      {
        type: REPORT_TYPE_PARITY,
        label: this.intl.t('reports.type.hotel_parity'),
      },
      {
        type: REPORT_TYPE_MARKETRATES,
        label: this.intl.t('reports.type.market_rates'),
      },
      {
        type: REPORT_TYPE_MARKETPARITY,
        label: this.intl.t('reports.type.market_parity'),
      },
      {
        type: REPORT_TYPE_REVENUE_INSIGHT,
        label: this.intl.t('reports.type.revenue_insight'),
      },
      {
        type: REPORT_TYPE_PARITY_INSIGHT,
        label: this.intl.t('settings.reports.parity_report'),
      },
    ];
  }

  @computed('ownReports', 'reportCategories.length')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedReportCategory() {
    return this.ownReports
      ? this.reportCategories?.[0]
      : this.reportCategories?.[this.reportCategories.length - 1];
  }

  @computed('reportTypes', 'type')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedReportType() {
    const { type } = this;
    return this.reportTypes.find((typeOption) => type === typeOption.type);
  }

  @action bulkDeleteSelectedReports(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.alerting
      .showWarningAlert({
        title: this.intl.t('general.are_you_sure').toString(),
        text: this.intl
          .t('settings.reports.warning.bulk_delete', {
            count: this.arrReportsSelectedForBulkDeletion?.length,
          })
          .toString(),
        buttons: {
          cancel: {
            text: this.intl.t('general.cancel').toString(),
          },
          confirm: {
            text: this.intl.t('general.delete').toString(),
          },
        },
      })
      .then((willDelete) => {
        if (willDelete && this.arrReportsSelectedForBulkDeletion?.length) {
          // This will be an array of promises to pass to ember RSVP
          const promises: Promise<Reportconfig>[] = [];

          this.arrReportsSelectedForBulkDeletion.forEach((reportGroupId) => {
            const reportGroupToDelete = (this.model?.reportgroups ?? []).filter(
              (reportGroup) => reportGroup.id === reportGroupId,
            );

            reportGroupToDelete.forEach((reportGroup) => {
              reportGroup.reports.forEach((report) => {
                promises.push(report.destroyRecord());
              });
            });
          });

          set(this.model, 'isLoading', true);

          allSettled(promises).then(() => {
            set(this, 'arrReportsSelectedForBulkDeletion', []);
            this.rowSelection = [];
            this.send('doRefresh');
          });
        }
      });
  }

  @action doRefresh(): void {
    this.send('refreshRoute');
  }

  @action filterReports(option: { ownReports: unknown }): void {
    set(this, 'ownReports', option.ownReports);
    set(this, 'page', 1);
  }

  @action filterType(option: { type: unknown }): void {
    set(this, 'type', option.type);
    set(this, 'page', 1);
  }

  @action selectAllReportsForBulkDeletion(
    areAllReportsSelected?: boolean,
  ): void {
    if (this.deletableReports?.length) {
      if (!areAllReportsSelected) {
        set(this, 'arrReportsSelectedForBulkDeletion', [
          ...this.deletableReports,
        ]);
      } else {
        set(this, 'arrReportsSelectedForBulkDeletion', []);
      }
    }
  }

  @action selectHotelOrMarket(hotelOrMarket: {
    hotel_id?: string;
    market_id?: string;
  }): void {
    if (hotelOrMarket.hotel_id) {
      set(this, 'marketId', null);
      set(this, 'hotelId', hotelOrMarket.hotel_id);
    } else if (hotelOrMarket.market_id) {
      set(this, 'hotelId', null);
      set(this, 'marketId', hotelOrMarket.market_id);
    } else {
      set(this, 'hotelId', null);
      set(this, 'marketId', null);
    }
    set(this, 'selectedHotelOrMarket', hotelOrMarket);
    set(this, 'page', 1);
  }

  @action selectUser(user: User): void {
    // user.id will be null if the default option is selected e.g. { email: 'All users'}
    if (user.id) {
      set(this, 'recipient', user.id);
    } else {
      set(this, 'recipient', null);
    }
    set(this, 'page', 1);
    set(this, 'selectedRecipient', user);
  }

  @action selectUserToImpersonate(user: User): void {
    // user.id will be null if the default option is selected e.g {email: 'No impersonation'}
    if (user.id) {
      set(this, 'impersonateId', user.id);
    } else {
      set(this, 'impersonateId', null);
    }
    set(this, 'page', 1);
    set(this, 'selectedImpersonator', user);
  }

  @action setPage(page: number): void {
    this.page = page;
  }

  // @ts-expect-error: Parameter 'rowSelection' implicitly has an 'any' type.
  @action setRowSelection(rowSelection): void {
    // Check if all rows are selected
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (rowSelection?.length === this.convertedModel.length) {
      this.selectAllReportsForBulkDeletion(false);
    }
    // Check if all rows are deselected
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    else if (rowSelection?.length === 0) {
      this.selectAllReportsForBulkDeletion(true);
    }
    // Check if a new row is selected
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    else if (rowSelection?.length > this.rowSelection?.length) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
      const lastSelectedRow = rowSelection[rowSelection.length - 1];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      this.toggleReportForBulkDeletion(lastSelectedRow?.reportGroup?.id);
    }
    // Check if a row is deselected
    else if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      rowSelection?.length > 0 &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      rowSelection?.length < this.rowSelection?.length
    ) {
      const lastDeselectedRow = this.rowSelection.find(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        (row) => !rowSelection.includes(row),
      );

      // @ts-expect-error: Property 'reportGroup' does not exist on type 'never'.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      this.toggleReportForBulkDeletion(lastDeselectedRow?.reportGroup?.id);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.rowSelection = rowSelection;
  }

  @action sortResults(column: {
    ascending?: boolean;
    columnKey: string;
    sorted?: boolean;
  }): void {
    if (column.sorted) {
      setProperties(this, {
        sortDirection: column.ascending ? 'asc' : 'desc',
        sortBy: column.columnKey,
        page: 1,
      });
    }
  }

  @action toggleReportForBulkDeletion(reportGroupId: string): void {
    if (this.arrReportsSelectedForBulkDeletion.indexOf(reportGroupId) === -1) {
      set(this, 'arrReportsSelectedForBulkDeletion', [
        ...this.arrReportsSelectedForBulkDeletion,
        reportGroupId,
      ]);
    } else {
      set(
        this,
        'arrReportsSelectedForBulkDeletion',
        this.arrReportsSelectedForBulkDeletion.filter(
          (reportId) => reportId !== reportGroupId,
        ),
      );
    }
  }
}
